import { chmod, mkdtemp, rm, writeFile } from "node:fs/promises";
import { tmpdir } from "node:os";
import { join } from "node:path";
import { query as claudeQuery, } from "@anthropic-ai/claude-agent-sdk";
import { materializeCliLaunchCommand } from "@wakeloop/adapter-core";
import { createClaudeAgentSdkToolActivityTracker } from "../output/claude-tool-activity.js";
import { classifyClaudeFailure } from "./claude-cli-transport.js";
import { resolveClaudeConversationPolicyOptions } from "./claude-conversation-policy.js";
import { resolveClaudePermissionMode, WAKELOOP_CLAUDE_ALLOWED_TOOLS, } from "./claude-permission-policy.js";
import { createClaudePreviewEmitter } from "./claude-preview-emitter.js";
export class ClaudeAgentSdkTransportError extends Error {
    code;
    errorType;
    providerAccessEvidence;
    constructor(input) {
        super(input.message);
        this.name = "ClaudeAgentSdkTransportError";
        this.code = input.code;
        this.errorType = input.errorType;
        if (input.providerAccessEvidence) {
            this.providerAccessEvidence = input.providerAccessEvidence;
        }
    }
}
// resultMessage.is_error can be true even when subtype is "success": the SDK
// type (SDKResultSuccess) carries is_error precisely because some failures
// never reach a dedicated error subtype (e.g. an OAuth session that expired
// before the first turn).
function isFailedResultMessage(resultMessage) {
    return resultMessage.subtype !== "success" || resultMessage.is_error === true;
}
export async function invokeClaudeAgentSdkTransport(input) {
    const prompt = normalizeOptionalText(input.prompt);
    if (!prompt) {
        throw new ClaudeAgentSdkTransportError({
            code: "schema.invalid",
            errorType: "config",
            message: "schema.invalid: prompt is required",
        });
    }
    const executable = await resolveClaudeAgentSdkExecutable({
        launchContract: input.launchContract,
    });
    const abortController = new AbortController();
    const invokedAtMs = Date.now();
    const previewEmitter = createClaudePreviewEmitter({
        onPreview: input.onPreview,
        streamingMode: input.streamingMode,
    });
    const providerErrorMessages = [];
    const stderrChunks = [];
    let firstOutputAtMs = null;
    let latestOutput = "";
    let resultMessage = null;
    let resolvedSessionId = normalizeOptionalText(input.sessionId);
    let timeoutTriggeredAtMs = null;
    let timeoutHandle = null;
    let queryRuntime = null;
    let unregisterQuery;
    const toolActivityTracker = createClaudeAgentSdkToolActivityTracker();
    if (input.claudeTimeoutMs !== null) {
        timeoutHandle = setTimeout(() => {
            timeoutTriggeredAtMs = Date.now();
            abortController.abort();
        }, input.claudeTimeoutMs);
    }
    try {
        const createQuery = input.createQuery ?? createDefaultClaudeAgentSdkQuery;
        queryRuntime = createQuery({
            options: buildClaudeAgentSdkOptions({
                abortController,
                agentControllerPermissionMode: input.agentControllerPermissionMode,
                claudeWorkdir: input.claudeWorkdir,
                conversationPolicy: input.conversationPolicy,
                env: input.env,
                executionMode: input.executionMode,
                onStderr: (chunk) => {
                    stderrChunks.push(chunk);
                },
                pathToClaudeCodeExecutable: executable.pathToClaudeCodeExecutable,
                sessionId: normalizeOptionalText(input.sessionId),
                streamingMode: input.streamingMode,
            }),
            prompt: createSinglePrompt(prompt),
        });
        if (input.taskId) {
            unregisterQuery = input.registerQuery?.({
                query: queryRuntime,
                taskId: input.taskId,
            });
        }
        for await (const message of queryRuntime) {
            await emitClaudeAgentSdkToolActivity({
                message,
                onRuntimeEvent: input.onRuntimeEvent,
                toolActivityTracker,
            });
            const nextState = handleClaudeAgentSdkMessage({
                firstOutputAtMs,
                latestOutput,
                message,
                previewEmitter,
                providerErrorMessages,
                resolvedSessionId,
                resultMessage,
            });
            firstOutputAtMs = nextState.firstOutputAtMs;
            latestOutput = nextState.latestOutput;
            resultMessage = nextState.resultMessage;
            resolvedSessionId = nextState.resolvedSessionId;
            if (nextState.observedConversationId) {
                await input.onRuntimeEvent?.({
                    agentControllerConversationId: nextState.observedConversationId,
                    phase: "provider_conversation_observed",
                    tsMs: Date.now(),
                });
            }
        }
    }
    catch (error) {
        throw toClaudeAgentSdkTransportError({
            error,
            firstOutputAtMs,
            providerErrorMessages,
            stderrText: stderrChunks.join(""),
            timeoutMs: input.claudeTimeoutMs,
            timeoutTriggeredAtMs,
        });
    }
    finally {
        if (timeoutHandle) {
            clearTimeout(timeoutHandle);
        }
        unregisterQuery?.();
        queryRuntime?.close();
        await executable.cleanup();
    }
    await previewEmitter.flush();
    if (!resultMessage) {
        const classifiedFailure = classifyClaudeFailure({
            providerErrorMessages,
            stderrText: stderrChunks.join(""),
        });
        throw new ClaudeAgentSdkTransportError({
            code: classifiedFailure.code,
            errorType: classifiedFailure.errorType,
            message: classifiedFailure.message,
            ...(classifiedFailure.providerAccessEvidence
                ? { providerAccessEvidence: classifiedFailure.providerAccessEvidence }
                : {}),
        });
    }
    // providerErrorMessages already has every candidate message
    // handleClaudeAgentSdkMessage collected (assistant.error,
    // result.result-when-is_error, result.errors), so classification only
    // needs that shared array here.
    if (isFailedResultMessage(resultMessage)) {
        const classifiedFailure = classifyClaudeFailure({
            providerErrorMessages,
            stderrText: stderrChunks.join(""),
        });
        throw new ClaudeAgentSdkTransportError({
            code: classifiedFailure.code,
            errorType: classifiedFailure.errorType,
            message: classifiedFailure.message,
            ...(classifiedFailure.providerAccessEvidence
                ? { providerAccessEvidence: classifiedFailure.providerAccessEvidence }
                : {}),
        });
    }
    const output = normalizeOptionalText(latestOutput);
    if (!output) {
        throw new ClaudeAgentSdkTransportError({
            code: "runtime.output.empty",
            errorType: "adapter",
            message: "runtime.output.empty: claude finished without agent output",
        });
    }
    return {
        output,
        sessionId: normalizeOptionalText(resolvedSessionId),
        telemetry: {
            completedAtMs: Date.now(),
            ...(firstOutputAtMs === null ? {} : { firstOutputAtMs }),
            invokedAtMs,
            previewEmitted: previewEmitter.emitted,
            resumedContext: normalizeOptionalText(input.sessionId) !== null,
            streamingMode: input.streamingMode ?? "final",
            ...(timeoutTriggeredAtMs === null ? {} : { timeoutTriggeredAtMs }),
        },
    };
}
export async function resolveClaudeAgentSdkExecutable(input) {
    const launchCommand = materializeCliLaunchCommand({
        launchContract: input.launchContract,
    });
    if (launchCommand.argsPrefix.length === 0 && launchCommand.shell !== true) {
        return {
            cleanup: async () => undefined,
            pathToClaudeCodeExecutable: launchCommand.bin,
        };
    }
    const wrapperDir = await mkdtemp(join(tmpdir(), "wakeloop-claude-sdk-"));
    const wrapperPath = process.platform === "win32"
        ? join(wrapperDir, "claude-code-wrapper.cmd")
        : join(wrapperDir, "claude-code-wrapper");
    await writeFile(wrapperPath, process.platform === "win32"
        ? buildWindowsWrapperScript({
            argsPrefix: launchCommand.argsPrefix,
            bin: launchCommand.bin,
        })
        : buildPosixWrapperScript({
            argsPrefix: launchCommand.argsPrefix,
            bin: launchCommand.bin,
        }), "utf8");
    if (process.platform !== "win32") {
        await chmod(wrapperPath, 0o755);
    }
    return {
        cleanup: async () => {
            await rm(wrapperDir, { force: true, recursive: true });
        },
        pathToClaudeCodeExecutable: wrapperPath,
    };
}
function buildClaudeAgentSdkOptions(input) {
    const permissionMode = resolveClaudePermissionMode({
        agentControllerPermissionMode: input.agentControllerPermissionMode,
        executionMode: input.executionMode,
    });
    const options = {
        abortController: input.abortController,
        additionalDirectories: [input.claudeWorkdir],
        allowDangerouslySkipPermissions: permissionMode === "bypassPermissions",
        allowedTools: [...WAKELOOP_CLAUDE_ALLOWED_TOOLS],
        cwd: input.claudeWorkdir,
        env: {
            ...process.env,
            ...(input.env ?? {}),
        },
        includePartialMessages: input.streamingMode === "stream",
        pathToClaudeCodeExecutable: input.pathToClaudeCodeExecutable,
        permissionMode,
        settingSources: ["user", "project", "local"],
        stderr: input.onStderr,
        systemPrompt: {
            preset: "claude_code",
            type: "preset",
        },
    };
    const sessionId = normalizeOptionalText(input.sessionId);
    if (sessionId) {
        options.resume = sessionId;
    }
    const conversationPolicyOptions = resolveClaudeConversationPolicyOptions(input.conversationPolicy);
    const model = conversationPolicyOptions.model;
    if (model) {
        options.model = model;
    }
    const fallbackModel = conversationPolicyOptions.fallbackModel;
    if (fallbackModel) {
        options.fallbackModel = fallbackModel;
    }
    const effort = conversationPolicyOptions.reasoningLevel;
    if (effort) {
        options.effort = effort;
    }
    return options;
}
function createDefaultClaudeAgentSdkQuery(input) {
    return claudeQuery(input);
}
async function* createSinglePrompt(prompt) {
    await Promise.resolve();
    yield {
        message: {
            content: prompt,
            role: "user",
        },
        parent_tool_use_id: null,
        type: "user",
    };
}
function handleClaudeAgentSdkMessage(input) {
    let firstOutputAtMs = input.firstOutputAtMs;
    let latestOutput = input.latestOutput;
    let observedConversationId = null;
    let resolvedSessionId = input.resolvedSessionId;
    let resultMessage = input.resultMessage;
    const sessionId = normalizeOptionalText(input.message.session_id);
    if (sessionId) {
        observedConversationId = sessionId;
    }
    const previewDeltaText = extractPreviewDeltaText(input.message);
    if (previewDeltaText) {
        const nextPreviewText = input.previewEmitter.append(previewDeltaText);
        if (nextPreviewText && firstOutputAtMs === null) {
            firstOutputAtMs = Date.now();
        }
    }
    if (input.message.type === "assistant" && input.message.error) {
        input.providerErrorMessages.push(input.message.error);
    }
    if (input.message.type === "result") {
        resultMessage = input.message;
        if (sessionId) {
            resolvedSessionId = sessionId;
        }
        if (input.message.subtype === "success" && !input.message.is_error) {
            latestOutput = input.message.result;
            if (firstOutputAtMs === null) {
                firstOutputAtMs = Date.now();
            }
        }
        else if (input.message.subtype === "success") {
            // SDKResultSuccess still carries is_error: some failures (e.g. an
            // OAuth session that expired before the first turn) are reported as
            // a "success" envelope whose `result` text is the actual error, not
            // agent output. Route that text into classification instead of
            // returning it to the user as a normal reply.
            input.providerErrorMessages.push(input.message.result);
        }
        else {
            input.providerErrorMessages.push(...input.message.errors);
        }
    }
    return {
        firstOutputAtMs,
        latestOutput,
        observedConversationId,
        resolvedSessionId,
        resultMessage,
    };
}
async function emitClaudeAgentSdkToolActivity(input) {
    for (const event of input.toolActivityTracker.observe(input.message)) {
        await input.onRuntimeEvent?.(event);
    }
}
function extractPreviewDeltaText(message) {
    if (message.type !== "stream_event") {
        return null;
    }
    const event = message.event;
    if (event.type === "content_block_delta" &&
        event.delta?.type === "text_delta" &&
        typeof event.delta.text === "string") {
        return event.delta.text;
    }
    return null;
}
function toClaudeAgentSdkTransportError(input) {
    if (input.error instanceof ClaudeAgentSdkTransportError) {
        return input.error;
    }
    if (input.timeoutTriggeredAtMs !== null) {
        const code = input.firstOutputAtMs === null
            ? "timed_out_before_output"
            : "timed_out_after_output";
        return new ClaudeAgentSdkTransportError({
            code,
            errorType: "timeout",
            message: `${code}: claude timed out after ${input.timeoutMs ?? 0}ms`,
        });
    }
    const fallbackMessage = input.error instanceof Error ? input.error.message : String(input.error);
    const classifiedFailure = classifyClaudeFailure({
        providerErrorMessages: [
            ...input.providerErrorMessages,
            normalizeOptionalText(fallbackMessage) ?? "",
        ],
        stderrText: input.stderrText,
    });
    return new ClaudeAgentSdkTransportError({
        code: classifiedFailure.code,
        errorType: classifiedFailure.errorType,
        message: classifiedFailure.message,
        ...(classifiedFailure.providerAccessEvidence
            ? { providerAccessEvidence: classifiedFailure.providerAccessEvidence }
            : {}),
    });
}
function buildPosixWrapperScript(input) {
    const command = [
        shellQuote(input.bin),
        ...input.argsPrefix.map(shellQuote),
        '"$@"',
    ].join(" ");
    return ["#!/bin/sh", `exec ${command}`, ""].join("\n");
}
function buildWindowsWrapperScript(input) {
    const command = [
        quoteWindowsArg(input.bin),
        ...input.argsPrefix.map(quoteWindowsArg),
        "%*",
    ].join(" ");
    return ["@echo off", command, ""].join("\r\n");
}
function shellQuote(value) {
    return `'${value.replaceAll("'", "'\\''")}'`;
}
function quoteWindowsArg(value) {
    return `"${value.replaceAll('"', '""')}"`;
}
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
